import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

// Per-booking projection of the money ledger (#2002), served by the
// new-backend admin endpoint `GET /admin/order/:orderId/payment-history`.
//
// The ledger is append-only and provider-agnostic, so new `eventType`s can
// appear before this UI knows them (a bank-transfer adapter, a new Stripe
// lifecycle step). Unknown types MUST render generically from the raw value
// rather than crash or silently vanish — a money event nobody can see is
// worse than one with an ugly label.

/** Mirrors `money_event_type_enum`; treat as open, not exhaustive. */
export type MoneyEventType =
    | 'charge_succeeded'
    | 'charge_failed'
    | 'charge_pending'
    | 'refund'
    | 'deposit_authorized'
    | 'deposit_captured'
    | 'deposit_released'
    | 'dispute_opened'
    | 'dispute_won'
    | 'dispute_lost'
    | 'checkout_expired'
    | string;

export interface PaymentHistoryEvent {
    id: string;
    eventType: MoneyEventType;
    /** Major units. Null for events that carry no amount. */
    amount: number | null;
    currency: string | null;
    /** 'stripe' | 'bank_transfer' | … — open by design. */
    provider: string;
    /** Durable provider object id: Checkout Session, Refund, Dispute, … */
    providerReference: string;
    lifecycleStep: string;
    /** When it happened at the provider. */
    occurredAt: string;
    /** When we recorded it. Differs from occurredAt after a replay. */
    recordedAt: string;
}

export interface PaymentHistorySummary {
    settled: number;
    refunded: number;
    disputed: number;
    captured: number;
    net: number;
    hasPending: boolean;
    hasFailed: boolean;
    hasDispute: boolean;
    /** Precedence collapse — lossy on purpose; the events carry the detail. */
    badge: string;
}

export interface PaymentHistoryResponse {
    orderId: string;
    /** `Order.payment_status`, so the UI can show a disagreement rather than hide it. */
    storedPaymentStatus: string | null;
    summary: PaymentHistorySummary;
    /**
     * Whether kolula's ledger is this booking's payment record. False for a
     * booking from before the tenant's `authoritativeFrom` date — its first
     * kolula invoice (ledger decision 5): its payments are in the tenant's own
     * accounting. The UI must say that — NOT "this booking was never paid".
     */
    authoritative: boolean;
    /** `YYYY-MM-DD`, Europe/Berlin — the issue day of the tenant's first kolula invoice;
     *  null before that (Mode C, or no invoice yet), on a legacy store, or when unknown. */
    authoritativeFrom: string | null;
    /** occurred_at ascending. */
    events: PaymentHistoryEvent[];
}

export const paymentHistoryApiSlice = createApi({
    reducerPath: 'paymentHistoryApiSlice',
    // The tenant belongs in the cache key: it travels as a header, so without
    // this every tenant shares one entry (see api/tenantCacheKey.ts, #1848).
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['PaymentHistory'],
    endpoints: (builder) => ({
        getPaymentHistory: builder.query<
            PaymentHistoryResponse,
            { orderId: string }
        >({
            query: ({ orderId }) => ({
                url: `/order/${orderId}/payment-history`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { orderId }) => [
                { type: 'PaymentHistory', id: orderId },
            ],
        }),
    }),
});

export const { useGetPaymentHistoryQuery, useLazyGetPaymentHistoryQuery } =
    paymentHistoryApiSlice;
